import { Injectable } from '@angular/core';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor, HttpResponse, HttpHeaders } from '@angular/common/http';
import { Observable, from, of, pipe, throwError } from 'rxjs';

import { AccountService, AlertService, AppTenantService } from '@app/_services';
import { catchError, mergeMap, switchMap } from 'rxjs/operators';
import { UtilsService } from 'abp-ng2-module/dist/src/utils/utils.service';
import { AppConsts } from '../../../src/shared/AppConsts';
import { SSOAuthService } from '@app/_services/sso.service';
import { StringUtils } from '@shared/utils/StringUtils';
import { Platform } from '@ionic/angular';
import { CookieAuthService } from '@app/_services/cookie-auth.service';
import { SslHttpClient } from '@app/_services/ssl.http-client';
import { CamsAuthService } from '@app/_services/cams.auth.service';

/**
 * 401 from cams-api or an internal service because the Wak.Token cookie is
 * missing/expired. The header is the contract; the body text is the fallback
 * for cross-origin responses that do not expose custom headers.
 */
export const SSO_SESSION_EXPIRED_MESSAGE = 'SSO session is missing or expired';
export function isSsoSessionExpired(error: any): boolean {
    if (!error || error.status !== 401) return false;
    if (error.headers && error.headers.get && error.headers.get('X-SSO-Session') === 'expired') return true;
    const body = error.error || {};
    return (
        body.code === 'SSO_SESSION_EXPIRED' ||
        body.message === SSO_SESSION_EXPIRED_MESSAGE ||
        (body.error && body.error.message === SSO_SESSION_EXPIRED_MESSAGE)
    );
}

@Injectable()
export class JwtInterceptor implements HttpInterceptor {
    defaultError = {
        message: 'An error has occurred!',
        details: 'Error details were not sent by server.'
    };

    constructor(
        private accountService: AccountService,
        private _appTenantService: AppTenantService,
        private _utilService: UtilsService,
        private alertService: AlertService,
        private ssoAuthService: SSOAuthService,
        private platform: Platform,
        public _cookieAuthService: CookieAuthService,
        private _camsAuthService: CamsAuthService,
        private sslHttpClient: SslHttpClient
    ) { }

    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        
        // Skip SSO authentication for public API endpoints and for calls to the SSO itself:
        // POST <sso>/oauth/refresh must not wait on ensureLoggedIn(), which is waiting on it (#2903).
        const ssoBase = (AppConsts.SSO.url || '').replace(/\/$/, '');
        if (request.url.includes(`/cams-service/token`) ||
            request.url.includes(`/core-service`) ||
            (ssoBase && request.url.startsWith(ssoBase))) {
            return this.handleHttpRequest(request, next);
        }
        
        // Skip authentication for public routes (like approve-prospect)
        const currentUrl = window.location.pathname;
        if (currentUrl.startsWith('/public/')) {
            return this.handleHttpRequest(request, next);
        }
        
        return from(
            this.ssoAuthService.ensureLoggedIn().pipe(
                switchMap(() => {
                    if (request.url.includes(AppConsts.remoteServiceBaseUrl)) {
                        return from(this._camsAuthService
                            .ensureLoggedIn(this._appTenantService.getTenant())
                            .pipe(switchMap((camsLoggedIn) => {
                                if (camsLoggedIn) {
                                    return this.handleHttpRequest(request, next);
                                } else {
                                    return throwError("Unauthorized");
                                }
                            })
                            ));
                    } else {
                        return this.handleHttpRequest(request, next);
                    }
                }),
                catchError((error) => {
                    return throwError(() => error);
                })
            )
        );
    }

    /**
    * Function to handle intercept requests, on Web it is called directly, in case of mobile
    * we're using 'cordova-plugin-sslsupport' for APIs calls, which uses native approach for both Android & iOS,
    * So if that fails for any reason other than SSL (Man-in-the-middle), we redirect the calls to here
    * to use Angular's httpClient instead to avoid user interruption.
    * @param req HttpRequest as get from intercept function
    * @param next HttpHandler as get from intercept function
    * @returns Observable to handle the request
    */
    handleHttpRequest(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        if (request.responseType !== 'blob' && StringUtils.isMobilePlatform(this.platform)) {
            return from(this.sslHttpClient.request(request, next).pipe(switchMap((result) => {
                // Here we return another observable to handle API calls through Angular's httpClient
                // if an API fails for any reason other than SSL (Man-in-the-middle).
                if (result && (result as any).sslFailed) {
                    // iOS can't access cookies, so we need to manually pass "Authorization" header
                    if (this.platform.is('cordova') && this.platform.is('ios')) {
                        request = request.clone({
                            setHeaders: {
                                Authorization: "Bearer " + (result as any).authToken,
                            },
                        });
                    }
                    return this.handleRequest(request, next);
                }
                return new Observable<HttpEvent<any>>((subs) => {
                    subs.next(result);
                    subs.complete();
                });
            })));
        } else {
            return this.handleRequest(request, next);
        }
    }

    handleRequest(request: HttpRequest<any>, next: HttpHandler, alreadyRetried = false): Observable<HttpEvent<any>> {
        // add tenantId in the request
        let tenantId = this._appTenantService.getTenant();
        if (tenantId) {
            request = request.clone({
                setHeaders: {
                    'Abp.TenantId': tenantId
                },
                withCredentials: true
            });
        }

        // add auth header with jwt if user is logged in and request is to the api url
        let token = this._utilService.getCookieValue("Abp.AuthToken");
        const isApiUrl = request.url.startsWith(AppConsts.remoteServiceBaseUrl);
        if (token && isApiUrl) {
            request = request.clone({
                setHeaders: {
                    Authorization: `Bearer ${token}`
                }
            });
        }

        // return next.handle(request);
        return next.handle(request).pipe(
            mergeMap(
                (event: any) => {
                    if (event instanceof HttpResponse) {
                        var result = event.body && event.body.result ? event.body.result : null;
                        let headers = new HttpHeaders({ 'Set-Cookie': 'Abp.AuthToken' });
                        event = result ? event.clone({ body: result, headers: headers }) : event;
                    }
                    return of(event);
                })
            // TODO: Add error handling here
        ).pipe(catchError((error: any) => {
            // The SSO session lapsed while the CAMS token is still fine: refresh the
            // cookies at the SSO and retry this request once (#2903). Web only — the
            // mobile app's SSO cookies are not in this webview.
            if (!alreadyRetried && !StringUtils.isMobilePlatform(this.platform) && isSsoSessionExpired(error)) {
                return this.ssoAuthService.refreshSession().pipe(
                    switchMap((ok) => {
                        if (!ok) {
                            this.ssoAuthService.redirectToLogout();
                            return throwError(() => error.statusText);
                        }
                        return this.handleRequest(request, next, true);
                    })
                );
            }
            if (error.status === 401 || error.status === 403) {
                // Skip logout for public routes and core-service APIs
                const currentUrl = window.location.pathname;
                if (currentUrl.startsWith('/public/') || request.url.includes('/core-service')) {
                    // Don't redirect to logout for public API calls
                    return throwError(() => error.statusText);
                }
                
                // In some cases we get 401 on logout API also, causing infinite API call loop, this condition is to avoid that
                if (!request.url.includes('account/logout')) {
                    this.accountService.logout(true);
                } else {
                    this.ssoAuthService.logout();
                }
            }
            else if (error.status === 429) {
                this.alertService.error(error.error);
            }
            else if (error) {
                this.alertService.error(error);
            }
            else if (error.error) {
                this.alertService.error(error.error);
            }
            else if (error.details) {
                this.alertService.error(error.details, error.message || this.defaultError.message);
            }
            else {
                this.alertService.error(error.message || this.defaultError.message);
            }

            return throwError(() => error.statusText);
        }));
    }
}